<template>
  <div
    class="summarization-body-container"
    @click="handleClick"
  >
    <div
      v-html="htmlContent"
    />
  </div>
</template>

<script setup lang="ts">
import DOMPurify from 'dompurify';
import { Marked } from 'marked';
import { computed } from 'vue';

type TProps = {
  content: string | null;
  enableTimeLink?: boolean;
};
type TEmits = {
  'click:playAt': [time: number];
}
const props = withDefaults(defineProps<TProps>(), {
  enableTimeLink: true,
});
const emit = defineEmits<TEmits>();

const marked = new Marked();

const htmlContent = computed(() => {
  marked.setOptions({
    breaks: true,
  });
  return marked.parse(DOMPurify.sanitize(props.content || ''));
});

const handleClick = (e) => {
  if (!e.target.classList.contains('time-link')) return;
  // NOTE: (HH:mm:ss) のフォーマットのテキストがクリックされたら指定時間から再生するイベントを発火
  const text = e.target.innerText;
  const matched = text.match(/^.?(\d{1,2}):(\d{1,2}):(\d{1,2}).?$|^.?(\d{1,2}):(\d{1,2}).?$/);
  if (matched != null) {
    const hours = parseInt(matched[1] || 0);
    const minutes = parseInt(matched[2] || matched[4]);
    const seconds = parseInt(matched[3] || matched[5]);
    const time = hours * 60 * 60 + minutes * 60 + seconds;
    emit('click:playAt', time);
  }
};

const textRendererMarkedExtension = {
  name: 'text',
  renderer(token) {
    // NOTE: "mm:ss"よりも"hh:mm:ss"の方を優先してマッチさせたいので、別々に書いている。
    const regexp = /^([^ |　]?\d{1,2}:\d{1,2}(:\d{1,2})?[^ |　]?)|([^ |　]?\d{1,2}:\d{1,2}(:\d{1,2})?[^ |　]?)$/g;
    if (token.text.match(regexp)) {
      return token.text.replace(regexp, '<span class="time-link">$&</span>');
    }

    return false;
  },
};

if (props.enableTimeLink) {
  marked.use({
    extensions: [textRendererMarkedExtension],
  });
}
</script>

<style lang="scss" scoped>
.summarization-body-container {
  :deep(.time-link) {
    color: $uranus;
    text-decoration: underline;
    cursor: pointer;
  }
  // NOTE: "saas-client/src/styles/style.scss"で設定されているスタイルが効いてしまって段落表現などがされなくなるので、リセットする。
  :deep(*) {
    padding: revert;
    margin: revert;
  }
  :deep(*) {
    margin-top: 0px;
    margin-bottom: 0px;
  }
  :deep(p) {
    margin-top: 0px;
    margin-bottom: 16px;
  }
  :deep(div > ul) {
    padding-left: 20px;
  }
}
</style>
